import {
  Alert,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import type { ActionSetInput, EditActionDto } from "../../../api/types";
import { referencesInput } from "../../../logic/action-set-template";

export type ActionSetInputsEditorProps = {
  inputs: ActionSetInput[];
  onChange: (inputs: ActionSetInput[]) => void;
  /** The set's own actions, to warn when a rename orphans a `{Navn}`. */
  actions: EditActionDto[];
  /** How many rules cite this set, for the same warning. */
  usedBy: number;
};

/** Recurses the way the template walk does, so a nested citation counts too. */
const usesInput = (actions: EditActionDto[], name: string): boolean =>
  actions.some((action) => {
    switch (action.field) {
      case "merchant":
      case "description":
        return referencesInput(action.value, name);
      case "prompt":
        return (
          action.value.prefix !== null &&
          referencesInput(action.value.prefix, name)
        );
      case "applyActionSet":
        return action.value.inputs.some((i) => referencesInput(i.value, name));
      case "conditional":
        return usesInput([action.value.action], name);
      case "match":
        return usesInput(
          action.value.arms.map((arm) => arm.action),
          name,
        );
      default:
        return false;
    }
  });

/**
 * Declares the holes this action set leaves for each citing rule to fill. An
 * action then writes `{Navn}` where the value goes, and the rule supplies it
 * once — which is what lets one "Månedsabonnement" set serve every
 * subscription instead of one set per subscription.
 */
export const ActionSetInputsEditor = ({
  inputs,
  onChange,
  actions,
  usedBy,
}: ActionSetInputsEditorProps) => {
  const update = (index: number, patch: Partial<ActionSetInput>) =>
    onChange(inputs.map((i, n) => (n === index ? { ...i, ...patch } : i)));

  // Renaming is delete-plus-add as far as stored rules are concerned: their
  // supplied values are keyed by name, so every citation goes blank. Say so
  // before the save rather than after.
  const orphaned = inputs.filter(
    (input) => input.name.trim() !== "" && !usesInput(actions, input.name),
  );

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Inputs</Typography>
      <Typography variant="caption" color="text.secondary">
        Values each rule using this set fills in. Write <code>{"{Navn}"}</code>{" "}
        in an action below to use one.
      </Typography>

      {inputs.map((input, index) => (
        <Stack
          key={index}
          direction="row"
          spacing={1}
          sx={{ alignItems: "flex-start" }}
        >
          <TextField
            size="small"
            label="Name"
            placeholder="Navn"
            value={input.name}
            onChange={(e) => update(index, { name: e.target.value })}
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            fullWidth
            label="Help text (optional)"
            placeholder="Hva abonnementet heter"
            value={input.description ?? ""}
            onChange={(e) =>
              update(index, { description: e.target.value || null })
            }
          />
          <IconButton
            size="small"
            aria-label={`Remove input ${input.name || index + 1}`}
            onClick={() => onChange(inputs.filter((_, n) => n !== index))}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ))}

      {orphaned.length > 0 && (
        <Alert severity="info">
          {orphaned.map((i) => `"${i.name}"`).join(", ")}{" "}
          {orphaned.length === 1 ? "is" : "are"} not used by any action here.
          {usedBy > 0 &&
            ` ${usedBy} ${usedBy === 1 ? "rule" : "rules"} will still be asked to fill ${orphaned.length === 1 ? "it" : "them"} in.`}
        </Alert>
      )}

      <Button
        startIcon={<AddIcon />}
        onClick={() => onChange([...inputs, { name: "", description: null }])}
        sx={{ alignSelf: "flex-start" }}
      >
        Add Input
      </Button>
    </Stack>
  );
};
